import React from "react";

import Box from "@busbud/design-system-components/dist/Box";
import CheckSM from "@busbud/design-system-components/dist/Icons/Check/SM";
import Label from "@busbud/design-system-components/dist/Label";

import { AmenitiesList } from "@app/components/amenities-list";
import { LocalizedPrice } from "@app/components/localized-price";
import { SelectableCard } from "@app/components/selectable-card/selectable-card";
import { useAppContext } from "@app/helpers/hooks";
import type { TripOption } from "@app/types/results-types";

interface Props {
  trip_class: TripOption;
  min_price_in_cents: number;
  selected: boolean;
  disabled?: boolean;
  onSelect: (classId: string) => void;
  preceding_class_name?: string;
  amenities: TripOption["amenities"];
}

const LowerTripClassLabel: React.FC<{
  name: TripOption["class_name"];
}> = ({ name }) => {
  const { translator } = useAppContext();

  return (
    <Box mb={1}>
      <Label
        size="md"
        fontWeight="bold"
        startIcon={<CheckSM />}
        color="text.tertiary.opaque"
      >
        {translator.t("!results.classes-fares.all-lower-class-amenities", {
          class_name: name
        })}
      </Label>
    </Box>
  );
};

export const ClassCard: React.FC<Props> = ({
  trip_class,
  min_price_in_cents,
  selected,
  onSelect,
  disabled,
  preceding_class_name,
  amenities
}) => {
  const _handleChange = React.useCallback(() => {
    onSelect(trip_class.trip_id);
  }, [onSelect, trip_class.trip_id]);

  return (
    <SelectableCard
      label={trip_class.class_name}
      extra={
        <Label
          size="lg"
          color="text.link.opaque"
          fontWeight="bold"
          data-testid="trip-class-option-price"
        >
          <LocalizedPrice price_in_cents={min_price_in_cents} />
        </Label>
      }
      checked={selected}
      onChange={_handleChange}
      disabled={disabled}
      dataCy="class-card"
    >
      {!!preceding_class_name && (
        <LowerTripClassLabel name={preceding_class_name} />
      )}
      {!!amenities.length && <AmenitiesList amenities={amenities} size="sm" />}
    </SelectableCard>
  );
};
